import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Boxes, Scissors, Users2, Receipt, ShoppingCart,
  Wallet, BarChart3, Settings as SettingsIcon, UserCog, LogOut, Menu, Banknote,
  ChevronLeft, ClipboardList,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ROLE_LABELS, ROLE_NAV } from '../lib/utils';
import logoMark from '../assets/logo_mark.png';

const NAV = [
  { to: '/dashboard', key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/inventory', key: 'inventory', label: 'Inventory', icon: Boxes },
  { to: '/production', key: 'production', label: 'Production', icon: Scissors },
  { to: '/my-work', key: 'my-work', label: 'My Timesheet', icon: ClipboardList },
  { to: '/my-cutting', key: 'my-cutting', label: 'My Cutting', icon: Scissors },
  { to: '/tailors', key: 'tailors', label: 'Tailors', icon: Users2 },
  { to: '/cutters', key: 'cutters', label: 'Cutters', icon: Scissors },
  { to: '/salary', key: 'salary', label: 'Salary', icon: Banknote },
  { to: '/sales', key: 'sales', label: 'Sales & Billing', icon: Receipt },
  { to: '/purchases', key: 'purchases', label: 'Purchases', icon: ShoppingCart },
  { to: '/expenses', key: 'expenses', label: 'Expenses', icon: Wallet },
  { to: '/reports', key: 'reports', label: 'Reports', icon: BarChart3 },
  { to: '/users', key: 'users', label: 'Users', icon: UserCog },
  { to: '/settings', key: 'settings', label: 'Settings', icon: SettingsIcon },
];

function allowed(role, key) {
  const nav = ROLE_NAV[role];
  if (nav === 'all') return true;
  if (Array.isArray(nav)) return nav.includes(key);
  return false;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV.filter((n) => allowed(user.role, n.key)).filter((n) => {
    if (n.key === 'my-work') return user.role === 'tailor';
    if (n.key === 'my-cutting') return user.role === 'cutter';
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        className={`no-print bg-[#0F172A] text-slate-300 flex flex-col transition-[width] duration-300 ease-in-out
          ${collapsed ? 'w-[68px]' : 'w-64'}
          ${mobileOpen ? 'fixed inset-y-0 left-0 z-40 w-64' : 'hidden md:flex'}`}
      >
        <div className="flex items-center gap-2.5 h-16 px-4 border-b border-white/10 shrink-0">
          <img src={logoMark} alt="Khayyat Al-Babji" className="w-9 h-9 rounded-md bg-white object-cover shrink-0" data-testid="sidebar-logo" />
          {!collapsed && (
            <div className="min-w-0">
              <p className="font-heading font-bold text-white text-sm leading-tight truncate">Khayyat Al-Babji</p>
              <p className="text-[11px] text-slate-400 leading-tight">Lil-Sayyidat · ERP</p>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {items.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive ? 'bg-[#1E3A8A] text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'
                } ${collapsed ? 'justify-center' : ''}`
              }
              title={n.label}
            >
              <n.icon size={18} strokeWidth={2} className="shrink-0" />
              {!collapsed && <span className="truncate">{n.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-2 border-t border-white/10">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-300 hover:bg-rose-500/10 hover:text-rose-300 transition-colors ${collapsed ? 'justify-center' : ''}`}
          >
            <LogOut size={18} strokeWidth={2} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="no-print h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-slate-500" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu size={22} />
            </button>
            <button
              className="hidden md:flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              onClick={() => setCollapsed((c) => !c)}
              data-testid="collapse-sidebar-btn"
            >
              <ChevronLeft size={18} className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
            </button>
            <div className="text-sm text-slate-400 hidden sm:block">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900 leading-tight">{user.name}</p>
              <p className="text-[11px] text-slate-400 leading-tight">{ROLE_LABELS[user.role]}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center font-heading font-bold text-sm">
              {user.name?.charAt(0)?.toUpperCase()}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-[1400px] mx-auto animate-fade-up">{children}</div>
        </main>
      </div>
    </div>
  );
}
